/* BP-016 — Lead Scoring Intelligence tab.
   Explainable 0-100 score, 8 weighted signals, priority tier, and PDF export. */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, RotateCcw, Download, Target, AlertTriangle, Clock, Zap, TrendingUp, Sparkles, ThumbsUp, ThumbsDown, Users, Check, X, Brain, ShieldCheck, Activity } from "lucide-react";


const PRIORITY_TW = {
  critical: { bg: "bg-red-50",     border: "border-red-400",     text: "text-red-700",     bar: "bg-red-500",     label: "CRITICAL" },
  high:     { bg: "bg-amber-50",   border: "border-amber-400",   text: "text-amber-700",   bar: "bg-amber-500",   label: "HIGH" },
  medium:   { bg: "bg-blue-50",    border: "border-blue-400",    text: "text-blue-700",    bar: "bg-blue-500",    label: "MEDIUM" },
  low:      { bg: "bg-slate-50",   border: "border-slate-300",   text: "text-slate-700",   bar: "bg-slate-400",   label: "LOW" },
};

const SLA_TW = {
  ok:       { bg: "bg-emerald-50", text: "text-emerald-700", ring: "ring-emerald-200" },
  warning:  { bg: "bg-amber-50",   text: "text-amber-700",   ring: "ring-amber-200"   },
  breached: { bg: "bg-red-50",     text: "text-red-700",     ring: "ring-red-300"     },
  "n/a":    { bg: "bg-slate-50",   text: "text-slate-600",   ring: "ring-slate-200"   },
};


export default function LeadScoringTab({ property }) {
  const [score, setScore] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  // Phase 2b Session 2 — Routing.
  const [routing, setRouting] = useState(null);
  const [routingLoading, setRoutingLoading] = useState(false);
  const [routingActing, setRoutingActing] = useState(false);
  // Phase 2b Session 2 — AI Coach.
  const [coach, setCoach] = useState(null);
  const [coachLoading, setCoachLoading] = useState(false);
  // Workspace-level dashboards.
  const [calibration, setCalibration] = useState(null);
  const [drift, setDrift] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [latestRes, histRes, calRes, drfRes] = await Promise.allSettled([
        api.get(`/v3/properties/${property.id}/lead-score/latest`),
        api.get(`/v3/properties/${property.id}/lead-score/history`),
        api.get(`/v3/lead-score/analytics/calibration`),
        api.get(`/v3/lead-score/analytics/drift?window_days=30`),
      ]);
      if (latestRes.status === "fulfilled") setScore(latestRes.value.data);
      else setScore(null);
      if (histRes.status === "fulfilled") setHistory(histRes.value.data.items || []);
      if (calRes.status === "fulfilled") setCalibration(calRes.value.data);
      if (drfRes.status === "fulfilled") setDrift(drfRes.value.data);
    } catch {
      toast.error("Failed to load Lead Score");
    } finally {
      setLoading(false);
    }
  }, [property.id]);

  useEffect(() => { load(); }, [load]);

  const calculate = useCallback(async () => {
    setCalculating(true);
    try {
      const { data } = await api.post(
        `/v3/properties/${property.id}/lead-score/calculate`, {});
      setScore(data);
      toast.success(`Lead Score: ${data.score.toFixed(1)} (${data.priority_label})`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Lead Score calculation failed");
    } finally {
      setCalculating(false);
    }
  }, [property.id, load]);

  const exportPdf = useCallback(() => {
    if (!score) return;
    window.open(
      `${process.env.REACT_APP_BACKEND_URL}/api/v3/properties/${property.id}/lead-score/report/pdf`,
      "_blank");
  }, [property.id, score]);

  const sendFeedback = useCallback(async (rating) => {
    if (!score?.id) return;
    try {
      await api.post(`/v3/lead-score/${score.id}/feedback`, { rating });
      toast.success(`Feedback recorded: ${rating}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Feedback failed");
    }
  }, [score]);

  // ── Routing (Phase 2b Session 2)
  const recommendRouting = useCallback(async () => {
    setRoutingLoading(true);
    try {
      const { data } = await api.post(
        `/v3/properties/${property.id}/lead-score/routing/recommend`, {});
      setRouting(data);
      toast.success(`Recommended assignee: ${data.top_candidate?.user_name || "—"}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Routing recommendation failed");
    } finally {
      setRoutingLoading(false);
    }
  }, [property.id]);

  const approveRouting = useCallback(async () => {
    if (!routing?.id) return;
    setRoutingActing(true);
    try {
      const { data } = await api.post(
        `/v3/lead-score/routing/${routing.id}/approve`, {});
      setRouting(data);
      toast.success("Assignment approved & applied.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Approve failed");
    } finally {
      setRoutingActing(false);
    }
  }, [routing]);

  const overrideRouting = useCallback(async (assignee_id, user_name) => {
    if (!routing?.id) return;
    setRoutingActing(true);
    try {
      const { data } = await api.post(
        `/v3/lead-score/routing/${routing.id}/override`, { assignee_id });
      setRouting(data);
      toast.success(`Overridden — assigned to ${user_name}.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Override failed");
    } finally {
      setRoutingActing(false);
    }
  }, [routing]);

  const rejectRouting = useCallback(async () => {
    if (!routing?.id) return;
    setRoutingActing(true);
    try {
      const { data } = await api.post(
        `/v3/lead-score/routing/${routing.id}/reject`, {});
      setRouting(data);
      toast.success("Recommendation rejected.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reject failed");
    } finally {
      setRoutingActing(false);
    }
  }, [routing]);

  // ── AI Coach (Phase 2b Session 2)
  const loadCoach = useCallback(async () => {
    if (!score?.id) return;
    try {
      const { data } = await api.get(`/v3/lead-score/${score.id}/coach`);
      setCoach(data);
    } catch { /* 404 is expected until first generation */ }
  }, [score]);

  useEffect(() => { loadCoach(); }, [loadCoach]);

  const generateCoach = useCallback(async (force = false) => {
    if (!score?.id) return;
    setCoachLoading(true);
    try {
      const { data } = await api.post(
        `/v3/lead-score/${score.id}/coach`, { force });
      setCoach(data);
      toast.success(data.cached
        ? "Coaching served from cache."
        : "AI coaching generated.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "AI Coach failed");
    } finally {
      setCoachLoading(false);
    }
  }, [score]);

  const band = useMemo(() =>
    score ? PRIORITY_TW[score.priority] || PRIORITY_TW.low : null,
  [score]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-slate-600 text-sm"
             data-testid="ls-loading">
        <Loader2 size={14} className="animate-spin"/> Loading…
      </div>);
  }

  return (
    <div className="space-y-4" data-testid="tab-panel-lead-score">
      {/* Header + actions */}
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-slate-500">
            <Target size={12}/> Lead Scoring Intelligence — Explainable Prioritization
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={calculate} disabled={calculating}
                    data-testid="ls-calc"
                    className="inline-flex items-center gap-1 rounded-md bg-slate-900 text-white text-xs font-semibold px-3 py-1.5 hover:bg-slate-700 disabled:opacity-50">
              {calculating ? <Loader2 size={12} className="animate-spin"/>
                             : <RotateCcw size={12}/>}
              {score ? "Recalculate" : "Calculate Lead Score"}
            </button>
            <button onClick={exportPdf} disabled={!score} data-testid="ls-pdf"
                    className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white text-slate-800 text-xs font-semibold px-3 py-1.5 hover:border-slate-400 disabled:opacity-40">
              <Download size={12}/> PDF
            </button>
          </div>
        </div>
        {!score && (
          <p className="mt-3 text-sm text-slate-600">
            No lead score yet. Click <b>Calculate Lead Score</b> to generate a
            deterministic 8-signal breakdown for this property.
          </p>
        )}
      </div>

      {score && band && (<>
        {/* Hero card */}
        <div className={`rounded-xl border ${band.border} ${band.bg} p-5`}
              data-testid="ls-hero">
          <div className="flex flex-wrap items-start justify-between gap-5">
            <div className="min-w-[240px]">
              <div className={`text-[10px] uppercase tracking-wider font-semibold ${band.text}`}>
                Lead Score
              </div>
              <div className="text-5xl font-bold tabular-nums mt-1 text-slate-900"
                    data-testid="ls-score">
                {Number(score.score).toFixed(1)}
              </div>
              <div className="text-xs text-slate-600 mt-1">out of 100</div>
              <div className={`inline-flex items-center gap-1 mt-3 rounded-full px-3 py-1 text-xs font-bold ${band.text} bg-white/70 border ${band.border}`}
                    data-testid="ls-priority-badge">
                <span className={`inline-block h-2 w-2 rounded-full ${band.bar}`}/>
                {band.label} PRIORITY
              </div>
              {score.sla && (() => {
                const sla = score.sla;
                const slaTW = SLA_TW[sla.breach_status] || SLA_TW["n/a"];
                return (
                  <div className={`mt-2 inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-[11px] font-medium ${slaTW.bg} ${slaTW.text} ring-1 ${slaTW.ring}`}
                        data-testid="ls-sla-badge">
                    <Clock size={11}/>
                    <span>{sla.state_label}</span>
                    {sla.breach_status !== "n/a" && (
                      <span className="tabular-nums">
                        · {sla.breach_status === "breached"
                            ? `overdue by ${Math.abs(sla.remaining_hours).toFixed(0)}h`
                            : `${sla.remaining_hours.toFixed(0)}h left`}
                      </span>
                    )}
                    <span className="uppercase tracking-wider text-[9px] font-bold opacity-80">
                      · {sla.breach_status}
                    </span>
                  </div>
                );
              })()}
              {score.recalc_reason?.startsWith("auto:") && (
                <div className="mt-2 inline-flex items-center gap-1 text-[10px] text-slate-500"
                      data-testid="ls-auto-updated">
                  <Zap size={10}/> Auto-updated on {score.recalc_reason.slice(5)}
                </div>
              )}
            </div>
            <div className="min-w-[220px] max-w-[380px]">
              <div className={`text-[10px] uppercase tracking-wider font-semibold ${band.text}`}>
                Recommended Actions
              </div>
              <ul className="mt-1 text-xs text-slate-700 space-y-1"
                   data-testid="ls-actions">
                {(score.recommended_actions || []).map((a, i) => (
                  <li key={i} className="flex gap-1.5">
                    <span className="text-slate-400">•</span>
                    <span>{a}</span>
                  </li>
                ))}
              </ul>
              {(score.data_gaps || []).length > 0 && (
                <div className="mt-2 flex items-center gap-1 text-[11px] text-red-700"
                      data-testid="ls-data-gaps">
                  <AlertTriangle size={11}/>
                  Data gaps: {score.data_gaps.join(", ")}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Phase 2b — Calibrated score + Probability funnel */}
        {(score.calibration || score.prediction) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* Calibration card */}
            {score.calibration && (
              <div className="rounded-xl border border-slate-200 bg-white p-4"
                    data-testid="ls-calibration">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-slate-500 mb-2">
                  <Sparkles size={11}/> Calibrated Score
                  {score.calibration.fallback && (
                    <span className="rounded-full px-2 py-0.5 text-[9px] font-bold bg-slate-100 text-slate-600">
                      BASE FALLBACK
                    </span>
                  )}
                </div>
                <div className="flex items-end gap-3">
                  <div className="text-3xl font-bold tabular-nums text-slate-900"
                        data-testid="ls-calibrated-score">
                    {score.calibration.calibrated_score.toFixed(1)}
                  </div>
                  <div className="flex-1">
                    <div className={`text-xs font-semibold ${score.calibration.delta >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                      {score.calibration.delta >= 0 ? "+" : ""}{score.calibration.delta.toFixed(1)} vs base
                    </div>
                    <div className="text-[10px] text-slate-500 mt-0.5">
                      ×{score.calibration.multiplier.toFixed(3)} · {score.calibration.sample_size} samples · {(score.calibration.confidence * 100).toFixed(0)}% confidence
                    </div>
                  </div>
                </div>
                <div className="mt-2 text-[10px] text-slate-500 italic">
                  {score.calibration.reason}
                </div>
              </div>
            )}
            {/* Probability funnel */}
            {score.prediction && (
              <div className="rounded-xl border border-slate-200 bg-white p-4"
                    data-testid="ls-prediction">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-slate-500 mb-2">
                  <TrendingUp size={11}/> Conversion Likelihood
                  {score.prediction.confidence_gated && (
                    <span className="rounded-full px-2 py-0.5 text-[9px] font-bold bg-amber-100 text-amber-700">
                      LOW CONFIDENCE
                    </span>
                  )}
                  <span className="ml-auto text-slate-500 text-[10px] tracking-normal normal-case">
                    n={score.prediction.sample_size} · {(score.prediction.confidence * 100).toFixed(0)}%
                  </span>
                </div>
                <div className="space-y-1.5">
                  {["response", "appointment", "contract", "close"].map((stage) => {
                    const s = score.prediction.stages?.[stage];
                    if (!s) return null;
                    return (
                      <div key={stage} className="grid grid-cols-12 gap-2 items-center"
                            data-testid={`ls-prob-${stage}`}>
                        <div className="col-span-3 text-xs text-slate-700 capitalize">{stage}</div>
                        <div className="col-span-7">
                          <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                            <div className={`h-full rounded-full ${band.bar}`}
                                  style={{ width: `${Math.max(2, s.probability * 100)}%` }}/>
                          </div>
                        </div>
                        <div className="col-span-2 text-right tabular-nums text-xs font-semibold text-slate-900">
                          {(s.probability * 100).toFixed(0)}%
                        </div>
                      </div>);
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Factor breakdown */}
        <div className="rounded-xl border border-slate-200 bg-white p-4"
              data-testid="ls-factors">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-3">
            Factor Breakdown — 8 Signals · Weighted 0-100
          </div>
          <div className="space-y-2">
            {Object.entries(score.signals || {}).map(([key, s]) => (
              <div key={key}
                    className="grid grid-cols-12 gap-3 items-center py-1.5"
                    data-testid={`ls-signal-${key}`}>
                <div className="col-span-4 text-xs font-medium text-slate-800">
                  {s.label}
                  <div className="text-[10px] text-slate-500 mt-0.5">
                    Weight {(s.weight * 100).toFixed(0)}%
                  </div>
                </div>
                <div className="col-span-5">
                  <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                    <div className={`h-full rounded-full ${band.bar}`}
                          style={{ width: `${Math.max(2, s.raw_value * 100)}%` }}/>
                  </div>
                  <div className="text-[10px] text-slate-500 mt-0.5">
                    {s.notes}
                  </div>
                </div>
                <div className="col-span-3 text-right tabular-nums">
                  <div className="text-sm font-semibold text-slate-900">
                    {s.contribution.toFixed(2)}
                  </div>
                  <div className="text-[10px] text-slate-500">
                    raw {Math.round(s.raw_value * 100)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* History */}
        {history.length > 1 && (
          <div className="rounded-xl border border-slate-200 bg-white p-4"
                data-testid="ls-history">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">
              Score History
            </div>
            <div className="text-xs">
              <div className="grid grid-cols-6 gap-2 font-semibold text-slate-500 py-1 border-b border-slate-100">
                <div>Version</div>
                <div>Score</div>
                <div>Priority</div>
                <div className="col-span-2">When</div>
                <div>Reason</div>
              </div>
              {history.slice(0, 10).map((h) => (
                <div key={h.history_id || h.id}
                      className="grid grid-cols-6 gap-2 py-1 border-b border-slate-100">
                  <div>v{h.version}</div>
                  <div className="tabular-nums">{Number(h.score).toFixed(1)}</div>
                  <div className={PRIORITY_TW[h.priority]?.text || ""}>
                    {h.priority_label}
                  </div>
                  <div className="col-span-2 text-slate-500">
                    {new Date(h.created_at).toLocaleString()}
                  </div>
                  <div className="text-slate-500">{h.recalc_reason || "—"}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Phase 2b Session 2 — Workload-Aware Routing */}
        <div className="rounded-xl border border-slate-200 bg-white p-4"
              data-testid="ls-routing">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-slate-500">
              <Users size={11}/> Workload-Aware Routing
              {routing && (
                <span className={`rounded-full px-2 py-0.5 text-[9px] font-bold ${
                  routing.status === "pending"  ? "bg-amber-100 text-amber-700" :
                  routing.status === "approved" ? "bg-emerald-100 text-emerald-700" :
                  routing.status === "override" ? "bg-blue-100 text-blue-700" :
                  "bg-slate-200 text-slate-600"}`}>
                  {routing.status.toUpperCase()}
                </span>
              )}
            </div>
            <button onClick={recommendRouting} disabled={routingLoading}
                    data-testid="ls-routing-recommend"
                    className="inline-flex items-center gap-1 rounded-md bg-slate-900 text-white text-xs font-semibold px-3 py-1.5 hover:bg-slate-700 disabled:opacity-50">
              {routingLoading ? <Loader2 size={12} className="animate-spin"/>
                               : <Target size={12}/>}
              {routing ? "Re-recommend" : "Recommend Assignee"}
            </button>
          </div>
          {!routing && (
            <p className="text-xs text-slate-600">
              Rank teammates by fit (35%), workload (25%), SLA (20%),
              conversion (15%), and availability (5%).
            </p>
          )}
          {routing && (
            <>
              {routing.top_candidate ? (
                <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3">
                  <div className="flex items-start justify-between flex-wrap gap-2">
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold">
                        Recommended
                      </div>
                      <div className="text-sm font-bold text-slate-900 mt-0.5"
                            data-testid="ls-routing-top">
                        {routing.top_candidate.user_name}
                        <span className="ml-2 text-[10px] text-slate-500 font-normal">
                          {routing.top_candidate.workspace_role}
                        </span>
                      </div>
                      <div className="text-[11px] text-slate-600 mt-1">
                        Composite {(routing.top_candidate.composite_score * 100).toFixed(0)}
                        {routing.top_delta > 0 && (
                          <span className="text-emerald-700 font-semibold">
                            {" "}· +{(routing.top_delta * 100).toFixed(0)} vs next
                          </span>
                        )}
                      </div>
                    </div>
                    {routing.status === "pending" && (
                      <div className="flex gap-1.5">
                        <button onClick={approveRouting} disabled={routingActing}
                                data-testid="ls-routing-approve"
                                className="inline-flex items-center gap-1 rounded-md bg-emerald-600 text-white text-xs font-semibold px-2.5 py-1 hover:bg-emerald-700 disabled:opacity-50">
                          <Check size={11}/> Approve
                        </button>
                        <button onClick={rejectRouting} disabled={routingActing}
                                data-testid="ls-routing-reject"
                                className="inline-flex items-center gap-1 rounded-md border border-red-200 bg-white text-red-700 text-xs font-semibold px-2.5 py-1 hover:bg-red-50 disabled:opacity-50">
                          <X size={11}/> Reject
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="mt-2 grid grid-cols-5 gap-2 text-[10px]">
                    {Object.entries(routing.top_candidate.sub_scores).map(([k, s]) => (
                      <div key={k} className="rounded bg-white/70 border border-emerald-100 px-1.5 py-1">
                        <div className="uppercase tracking-wider text-slate-500">{k}</div>
                        <div className="tabular-nums font-semibold text-slate-800">
                          {(s.value * 100).toFixed(0)}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-2 text-[10px] text-slate-500 italic">
                    {routing.reason}
                  </div>
                </div>
              ) : (
                <div className="text-xs text-slate-600">
                  No candidates available in this workspace.
                </div>
              )}

              {/* Ranked list — allow override picks */}
              {routing.candidates?.length > 1 && (
                <div className="mt-3">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5">
                    Alternatives ({routing.candidates.length - 1})
                  </div>
                  <div className="space-y-1">
                    {routing.candidates.slice(1).map((c) => (
                      <div key={c.user_id}
                            className="flex items-center justify-between rounded-md border border-slate-200 bg-slate-50 px-2.5 py-1.5"
                            data-testid={`ls-routing-alt-${c.user_id}`}>
                        <div className="text-xs text-slate-800">
                          {c.user_name}
                          <span className="ml-2 text-[10px] text-slate-500">
                            {(c.composite_score * 100).toFixed(0)} · {c.active_count} active
                          </span>
                        </div>
                        {routing.status === "pending" && (
                          <button onClick={() => overrideRouting(c.user_id, c.user_name)}
                                  disabled={routingActing}
                                  className="text-[11px] rounded border border-blue-200 bg-white text-blue-700 font-semibold px-2 py-0.5 hover:bg-blue-50 disabled:opacity-50">
                            Assign
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Phase 2b Session 2 — AI Coach */}
        <div className="rounded-xl border border-slate-200 bg-white p-4"
              data-testid="ls-coach">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-slate-500">
              <Brain size={11}/> AI Coach Insights
              {coach && coach.cached && (
                <span className="rounded-full px-2 py-0.5 text-[9px] font-bold bg-slate-100 text-slate-600">
                  CACHED
                </span>
              )}
              {coach && (
                <span className="rounded-full px-2 py-0.5 text-[9px] font-bold bg-indigo-100 text-indigo-700">
                  {(coach.confidence * 100).toFixed(0)}% confidence
                </span>
              )}
            </div>
            <div className="flex gap-1.5">
              <button onClick={() => generateCoach(false)} disabled={coachLoading}
                      data-testid="ls-coach-generate"
                      className="inline-flex items-center gap-1 rounded-md bg-slate-900 text-white text-xs font-semibold px-3 py-1.5 hover:bg-slate-700 disabled:opacity-50">
                {coachLoading ? <Loader2 size={12} className="animate-spin"/>
                              : <Sparkles size={12}/>}
                {coach ? "Refresh Coaching" : "Generate Coaching"}
              </button>
              {coach && (
                <button onClick={() => generateCoach(true)} disabled={coachLoading}
                        data-testid="ls-coach-force"
                        title="Bypass cache"
                        className="rounded-md border border-slate-200 bg-white text-slate-700 text-xs font-semibold px-2 py-1.5 hover:border-slate-400 disabled:opacity-40">
                  <RotateCcw size={12}/>
                </button>
              )}
            </div>
          </div>
          {!coach && (
            <p className="text-xs text-slate-600">
              Claude Sonnet 4.6 interprets the deterministic score and cites
              evidence paths for every claim. Cached by input hash.
            </p>
          )}
          {coach && (
            <div className="space-y-3">
              <div className="text-sm font-semibold text-slate-900"
                    data-testid="ls-coach-headline">
                {coach.headline}
              </div>
              {coach.strengths?.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold mb-1">
                    Strengths
                  </div>
                  <ul className="space-y-1 text-xs text-slate-700"
                       data-testid="ls-coach-strengths">
                    {coach.strengths.map((s, i) => (
                      <li key={i} className="flex gap-1.5">
                        <span className="text-emerald-500">•</span>
                        <span>
                          {s.text}
                          {s.evidence_paths?.length > 0 && (
                            <span className="ml-1 text-[10px] text-slate-400">
                              [{s.evidence_paths.join(", ")}]
                            </span>
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {coach.coaching_opportunities?.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold mb-1">
                    Opportunities
                  </div>
                  <ul className="space-y-1 text-xs text-slate-700"
                       data-testid="ls-coach-opps">
                    {coach.coaching_opportunities.map((o, i) => (
                      <li key={i} className="flex gap-1.5">
                        <span className="text-amber-500">•</span>
                        <span>
                          {o.text}
                          {o.evidence_paths?.length > 0 && (
                            <span className="ml-1 text-[10px] text-slate-400">
                              [{o.evidence_paths.join(", ")}]
                            </span>
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {coach.recommended_actions?.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-blue-700 font-semibold mb-1">
                    Recommended Actions
                  </div>
                  <ul className="space-y-1 text-xs text-slate-700"
                       data-testid="ls-coach-actions">
                    {coach.recommended_actions.map((a, i) => (
                      <li key={i} className="flex gap-1.5">
                        <span className="text-blue-500">•</span>
                        <span>
                          {a.text}
                          {a.evidence_paths?.length > 0 && (
                            <span className="ml-1 text-[10px] text-slate-400">
                              [{a.evidence_paths.join(", ")}]
                            </span>
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="text-[10px] text-slate-500 italic pt-1 border-t border-slate-100">
                {coach.disclaimer}
              </div>
            </div>
          )}
        </div>

        {/* Workspace-level Calibration + Drift summaries */}
        {(calibration || drift) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {calibration && (
              <div className="rounded-xl border border-slate-200 bg-white p-4"
                    data-testid="ls-workspace-calibration">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-slate-500 mb-2">
                  <ShieldCheck size={11}/> Workspace Calibration
                </div>
                <div className="text-xs text-slate-700">
                  {calibration.active ? (
                    <>
                      Active profile <span className="font-semibold">v{calibration.active.version}</span>
                      {" · "}{calibration.active.bucket_count} buckets
                      {" · "}{calibration.active.total_sample_size} samples
                    </>
                  ) : (
                    <span className="text-slate-500">No active profile yet.</span>
                  )}
                </div>
                <div className="text-[11px] text-slate-500 mt-1">
                  {calibration.profile_count} profile{calibration.profile_count === 1 ? "" : "s"} on file
                </div>
              </div>
            )}
            {drift && (
              <div className="rounded-xl border border-slate-200 bg-white p-4"
                    data-testid="ls-workspace-drift">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-slate-500 mb-2">
                  <Activity size={11}/> Score Drift (30d)
                </div>
                <div className="text-xs text-slate-700">
                  <span className="font-semibold">{drift.properties}</span> properties
                  {" · "}avg Δ <span className="tabular-nums font-semibold">{Number(drift.avg_delta || 0).toFixed(1)}</span>
                  {" · "}max Δ <span className="tabular-nums font-semibold">{Number(drift.max_delta || 0).toFixed(1)}</span>
                </div>
                {drift.top_movers?.length > 0 && (
                  <div className="mt-2 text-[10px] text-slate-500">
                    Top mover: {drift.top_movers[0].property_id.slice(0, 8)}…
                    {" (max Δ "}{drift.top_movers[0].max_delta.toFixed(1)}{")"}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Feedback bar */}
        <div className="rounded-xl border border-slate-200 bg-white p-4 flex flex-wrap items-center justify-between gap-3"
              data-testid="ls-feedback">
          <div className="text-xs text-slate-600">
            <span className="uppercase tracking-wider text-[10px] text-slate-500 mr-2">Score accuracy</span>
            Was this score right for the property?
          </div>
          <div className="flex gap-2">
            <button onClick={() => sendFeedback("accurate")} data-testid="ls-feedback-accurate"
                    className="inline-flex items-center gap-1 rounded-md border border-emerald-200 bg-emerald-50 text-emerald-700 text-xs font-semibold px-3 py-1.5 hover:bg-emerald-100">
              <ThumbsUp size={12}/> Accurate
            </button>
            <button onClick={() => sendFeedback("overscored")} data-testid="ls-feedback-over"
                    className="inline-flex items-center gap-1 rounded-md border border-amber-200 bg-amber-50 text-amber-700 text-xs font-semibold px-3 py-1.5 hover:bg-amber-100">
              Too high
            </button>
            <button onClick={() => sendFeedback("underscored")} data-testid="ls-feedback-under"
                    className="inline-flex items-center gap-1 rounded-md border border-blue-200 bg-blue-50 text-blue-700 text-xs font-semibold px-3 py-1.5 hover:bg-blue-100">
              Too low
            </button>
            <button onClick={() => sendFeedback("inaccurate")} data-testid="ls-feedback-inaccurate"
                    className="inline-flex items-center gap-1 rounded-md border border-red-200 bg-red-50 text-red-700 text-xs font-semibold px-3 py-1.5 hover:bg-red-100">
              <ThumbsDown size={12}/> Inaccurate
            </button>
          </div>
        </div>
      </>)}
    </div>
  );
}
